import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CreditCard, Users, UserCheck, UserCog, Trash2, AlertTriangle, Eye, Receipt } from 'lucide-react';
import type { GymAdminSummary, GymDetailView, GymPayment, GymPaymentRow, PaymentMethod, SubscriptionStatus } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Skeleton, EmptyState, Modal, Input, cn } from '@techsofit/ui';
import { useAsync } from '../lib/useAsync';
import { getGymDetail, getGymMembers, getGymStaff, getGymPayments, getGymMemberPayments, deleteGym, openSupportView } from '../lib/adminApi';
import { ManageModal } from './Billing';
import { money } from '../lib/format';

const SUB_STYLE: Record<SubscriptionStatus, string> = {
  ACTIVE: 'bg-ok-soft text-ok-ink',
  TRIAL: 'bg-neutral-soft text-neutral-ink',
  PAST_DUE: 'bg-bad-soft text-bad-ink',
  CANCELED: 'bg-neutral-soft text-neutral-ink',
};
const SUB_LABEL: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Active', TRIAL: 'Free trial', PAST_DUE: 'Past due', CANCELED: 'Canceled',
};

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

type Tab = 'overview' | 'members' | 'staff' | 'payments';
const TABS: Tab[] = ['overview', 'members', 'staff', 'payments'];

const PAY_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'ECOCASH', label: 'EcoCash' },
  { value: 'ONEMONEY', label: 'OneMoney' },
  { value: 'INNBUCKS', label: 'InnBucks' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'OTHER', label: 'Other' },
];
function payMethod(m: PaymentMethod) {
  return PAY_METHODS.find((x) => x.value === m)?.label ?? m;
}

export function GymDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: gym, loading, error, reload } = useAsync(() => getGymDetail(id), [id]);
  const tabParam = searchParams.get('tab');
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'overview';
  const setTab = (next: Tab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'overview') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
  };
  const [managing, setManaging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openingSupport, setOpeningSupport] = useState(false);

  if (loading) {
    return (
      <section className="mt-6 animate-fade-up">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-4 h-64" />
      </section>
    );
  }
  if (error || !gym) {
    return (
      <section className="mt-6 animate-fade-up">
        <EmptyState title="Couldn’t load this gym" description={error ?? 'It may have been removed.'} />
      </section>
    );
  }

  const summary: GymAdminSummary = {
    id: gym.id, name: gym.name, slug: gym.slug, status: gym.status,
    subscriptionStatus: gym.subscriptionStatus, planName: gym.planName, paidPlan: gym.paidPlan,
    billingCycle: gym.billingCycle, memberCount: gym.memberCount, currentPeriodEnd: gym.currentPeriodEnd,
  };

  return (
    <section className="mt-6 animate-fade-up">
      <button onClick={() => navigate('/billing')} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Gyms
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-display-md text-ink">{gym.name}</h1>
            <span className={cn('inline-flex rounded-pill px-2.5 py-1 text-xs font-medium', SUB_STYLE[gym.subscriptionStatus])}>
              {SUB_LABEL[gym.subscriptionStatus]}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted">
            {gym.type === 'SOLO_TRAINER' ? 'Solo trainer' : 'Gym'} · {gym.slug}.gyms.techsofit.com
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            loading={openingSupport}
            onClick={async () => {
              setOpeningSupport(true);
              try {
                const { url } = await openSupportView(gym.id);
                window.location.assign(url);
              } catch {
                setOpeningSupport(false);
              }
            }}
          >
            <Eye className="size-4" aria-hidden /> Open support view
          </Button>
          <Button onClick={() => setManaging(true)}>
            <CreditCard className="size-4" aria-hidden /> Manage billing
          </Button>
        </div>
      </div>

      <div className="mt-5 flex gap-1 border-b border-line">
        <TabBtn label="Overview" active={tab === 'overview'} onClick={() => setTab('overview')} />
        <TabBtn label={`Members${gym.memberCount ? ` (${gym.memberCount})` : ''}`} active={tab === 'members'} onClick={() => setTab('members')} />
        <TabBtn label={`Staff${gym.staffCount ? ` (${gym.staffCount})` : ''}`} active={tab === 'staff'} onClick={() => setTab('staff')} />
        <TabBtn label="Payments" active={tab === 'payments'} onClick={() => setTab('payments')} />
      </div>

      <div className="mt-4">
        {tab === 'overview' && <Overview gym={gym} />}
        {tab === 'members' && <MembersTab id={id} />}
        {tab === 'staff' && <StaffTab id={id} />}
        {tab === 'payments' && <PaymentsTab id={id} />}
      </div>

      {/* Danger zone — permanent, irreversible deletion. */}
      <Card className="mt-8 border border-bad-soft p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-1.5 font-display text-base font-semibold text-bad-ink">
              <AlertTriangle className="size-4" aria-hidden /> Delete this gym
            </h2>
            <p className="mt-1 max-w-prose text-sm text-muted">
              Permanently removes the gym and <strong>everything</strong> associated with it — members, payments,
              plans, workouts, goals, attendance, guest passes, staff roles and its subscription. This cannot be undone.
            </p>
          </div>
          <Button variant="outline" onClick={() => setDeleting(true)} className="shrink-0 border-bad-soft text-bad-ink hover:bg-bad-soft">
            <Trash2 className="size-4" aria-hidden /> Delete gym
          </Button>
        </div>
      </Card>

      {managing && (
        <ManageModal
          gym={summary}
          onClose={() => setManaging(false)}
          onSaved={() => { setManaging(false); reload(); }}
        />
      )}

      {deleting && (
        <DeleteGymModal
          gym={gym}
          onClose={() => setDeleting(false)}
          onDeleted={() => navigate('/billing')}
        />
      )}
    </section>
  );
}

function DeleteGymModal({ gym, onClose, onDeleted }: { gym: GymDetailView; onClose: () => void; onDeleted: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = name.trim().toLowerCase() === gym.name.trim().toLowerCase();

  async function confirmDelete() {
    if (!matches) return;
    setBusy(true);
    setError(null);
    try {
      await deleteGym(gym.id, name.trim());
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not delete the gym. Please try again.');
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="Delete gym" description="This permanently deletes the gym and all of its data. There is no undo.">
      <div className="space-y-4">
        <div className="rounded-control bg-bad-soft px-3.5 py-3 text-sm text-bad-ink">
          <p className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              Members, payments, plans, workouts, goals, attendance, guest passes, staff roles and the subscription
              for <strong>{gym.name}</strong> will be erased. Member/owner logins are kept but lose access.
            </span>
          </p>
        </div>
        <Input
          label={`Type “${gym.name}” to confirm`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={gym.name}
          autoFocus
        />
        {error && (
          <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2.5 text-sm font-medium text-bad-ink">{error}</p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={!matches || busy} loading={busy} className="bg-bad-ink text-on-dark hover:bg-bad-ink/90">
            <Trash2 className="size-4" aria-hidden /> Delete permanently
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TabBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('-mb-px shrink-0 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors',
        active ? 'border-ink text-ink' : 'border-transparent text-muted hover:text-ink')}
    >
      {label}
    </button>
  );
}

function Overview({ gym }: { gym: GymDetailView }) {
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'Joined', value: fmtDateTime(gym.createdAt) },
    { label: 'Registered by', value: gym.ownerName ? <span>{gym.ownerName}{gym.ownerEmail ? <span className="text-muted"> · {gym.ownerEmail}</span> : null}</span> : '—' },
    { label: 'Type', value: gym.type === 'SOLO_TRAINER' ? 'Solo trainer' : 'Gym' },
    { label: 'Members', value: `${gym.memberCount} total · ${gym.activeMemberCount} active` },
    { label: 'Staff', value: String(gym.staffCount) },
    { label: 'Plan', value: `${gym.planName}${gym.paidPlan ? ` · ${gym.billingCycle.toLowerCase()}` : ''}` },
    { label: 'Subscription', value: SUB_LABEL[gym.subscriptionStatus] },
    { label: gym.subscriptionStatus === 'TRIAL' ? 'Trial ends' : 'Renews', value: gym.subscriptionStatus === 'TRIAL' ? fmtDate(gym.trialEndsAt) : fmtDate(gym.currentPeriodEnd) },
    { label: 'Web address', value: `${gym.slug}.gyms.techsofit.com` },
    { label: 'Contact', value: gym.contactPhone || '—' },
    { label: 'Country', value: gym.country || '—' },
    { label: 'Signup source', value: gym.signupSource || 'Direct' },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon={<Users className="size-4" aria-hidden />} label="Members" value={gym.memberCount} hint="on the roster" />
        <StatCard icon={<UserCheck className="size-4" aria-hidden />} label="Active" value={gym.activeMemberCount} hint="currently active" accent />
        <StatCard icon={<UserCog className="size-4" aria-hidden />} label="Staff" value={gym.staffCount} hint="owner & staff" />
        <StatCard icon={<CreditCard className="size-4" aria-hidden />} label="Plan" value={gym.planName} hint={SUB_LABEL[gym.subscriptionStatus]} />
      </div>
      <Card className="divide-y divide-line p-0">
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-[10rem_1fr] gap-3 px-5 py-3 text-sm">
            <span className="text-muted">{r.label}</span>
            <span className="text-ink">{r.value}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function StatCard({ icon, label, value, hint, accent }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; hint?: string; accent?: boolean;
}) {
  return (
    <Card className={cn('p-4', accent && 'bg-accent text-accent-ink')}>
      <div className={cn('flex items-center gap-1.5 text-xs font-medium', accent ? 'text-accent-ink/80' : 'text-muted')}>
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-2 truncate font-display text-2xl font-semibold leading-none">{value}</p>
      {hint && <p className={cn('mt-1 truncate text-xs', accent ? 'text-accent-ink/70' : 'text-muted')}>{hint}</p>}
    </Card>
  );
}

function MembersTab({ id }: { id: string }) {
  const { data, loading, error } = useAsync(() => getGymMembers(id), [id]);
  if (loading) return <Skeleton className="h-40" />;
  if (error) return <EmptyState title="Couldn’t load members" description={error} />;
  if (!data || data.length === 0) return <EmptyState title="No members" description="This gym hasn’t added any members yet." />;
  return (
    <Card className="divide-y divide-line p-0">
      {data.map((m) => (
        <div key={m.id} className="flex items-center justify-between gap-3 px-5 py-3">
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">{m.fullName}</p>
            <p className="truncate text-sm text-muted">{m.email || m.phone || '—'} · joined {fmtDate(m.joinDate)}</p>
          </div>
          <div className="shrink-0 text-right text-sm">
            <span className={cn('inline-flex rounded-pill px-2 py-0.5 text-xs font-medium', m.active ? 'bg-ok-soft text-ok-ink' : 'bg-neutral-soft text-neutral-ink')}>
              {m.active ? 'Active' : 'Inactive'}
            </span>
            <p className="mt-0.5 text-muted">expires {fmtDate(m.currentExpiry)}</p>
          </div>
        </div>
      ))}
    </Card>
  );
}

function StaffTab({ id }: { id: string }) {
  const { data, loading, error } = useAsync(() => getGymStaff(id), [id]);
  if (loading) return <Skeleton className="h-40" />;
  if (error) return <EmptyState title="Couldn’t load staff" description={error} />;
  if (!data || data.length === 0) return <EmptyState title="No staff" description="No accounts on this gym yet." />;
  const roleLabel = (r: string) => r === 'GYM_OWNER' ? 'Owner' : r === 'GYM_STAFF' ? 'Staff' : r;
  return (
    <Card className="divide-y divide-line p-0">
      {data.map((u) => (
        <div key={u.id} className="flex items-center justify-between gap-3 px-5 py-3">
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">
              {u.fullName}
              {u.coach && <span className="ml-2 rounded-pill bg-neutral-soft px-1.5 py-0.5 text-xs font-medium text-neutral-ink">Coach</span>}
            </p>
            <p className="truncate text-sm text-muted">{u.email} · {roleLabel(u.role)}</p>
          </div>
          <div className="shrink-0 text-right text-sm">
            <span className={cn('inline-flex rounded-pill px-2 py-0.5 text-xs font-medium', u.status === 'ACTIVE' ? 'bg-ok-soft text-ok-ink' : 'bg-neutral-soft text-neutral-ink')}>
              {u.status === 'ACTIVE' ? 'Active' : u.status === 'INVITED' ? 'Invited' : 'Disabled'}
            </span>
            <p className="mt-0.5 text-muted">{u.lastLoginAt ? `last in ${fmtDateTime(u.lastLoginAt)}` : 'never signed in'}</p>
          </div>
        </div>
      ))}
    </Card>
  );
}

type PayView = 'subscription' | 'members';

function PaymentsTab({ id }: { id: string }) {
  const [view, setView] = useState<PayView>('subscription');
  const sub = useAsync(() => getGymPayments(id), [id]);
  const members = useAsync(() => getGymMemberPayments(id), [id]);

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-line">
        <TabBtn label="Subscription" active={view === 'subscription'} onClick={() => setView('subscription')} />
        <TabBtn label="Member payments" active={view === 'members'} onClick={() => setView('members')} />
      </div>
      {view === 'subscription' ? (
        <SubscriptionPayments data={sub.data} loading={sub.loading} error={sub.error} />
      ) : (
        <MemberPayments data={members.data} loading={members.loading} error={members.error} />
      )}
    </div>
  );
}

function SubscriptionPayments({ data, loading, error }: { data: GymPayment[] | null; loading: boolean; error: string | null }) {
  const rows = data ?? [];
  const collected = useMemo(
    () => rows.reduce((sum, p) => sum + Number(p.amount), 0),
    [rows],
  );
  if (loading) return <Skeleton className="h-40" />;
  if (error) return <EmptyState title="Couldn’t load payments" description={error} />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<CreditCard className="size-6" aria-hidden />}
        title="No subscription payments"
        description="Nothing recorded for this gym’s TechSoFit plan yet."
      />
    );
  }
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
        <p className="text-sm text-muted">{rows.length} payment{rows.length === 1 ? '' : 's'}</p>
        <p className="text-sm font-medium tabular text-ink">Collected {money(collected)}</p>
      </div>
      <div className="hidden grid-cols-[1fr_1fr_0.8fr_0.8fr] gap-4 border-b border-line px-5 py-2 text-eyebrow uppercase text-muted md:grid">
        <span>Plan</span>
        <span>Paid on</span>
        <span>Method</span>
        <span className="text-right">Amount</span>
      </div>
      <ul className="divide-y divide-line">
        {rows.map((p) => (
          <li key={p.id} className="grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-3 md:grid-cols-[1fr_1fr_0.8fr_0.8fr]">
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{p.planName}</p>
              <p className="truncate text-sm text-muted md:hidden">{fmtDate(p.paidOn)} · {payMethod(p.method)}</p>
              {p.note && <p className="mt-0.5 truncate text-xs text-muted">{p.note}</p>}
            </div>
            <span className="hidden text-sm tabular text-ink-soft md:block">{fmtDate(p.paidOn)}</span>
            <span className="hidden text-sm text-ink-soft md:block">{payMethod(p.method)}</span>
            <span className="justify-self-end text-sm font-medium tabular text-ink">{money(p.amount)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function MemberPayments({ data, loading, error }: { data: GymPaymentRow[] | null; loading: boolean; error: string | null }) {
  const rows = data ?? [];
  const collected = useMemo(
    () => rows.filter((p) => !p.voided).reduce((sum, p) => sum + Number(p.amount), 0),
    [rows],
  );
  if (loading) return <Skeleton className="h-40" />;
  if (error) return <EmptyState title="Couldn’t load payments" description={error} />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Receipt className="size-6" aria-hidden />}
        title="No member payments"
        description="This gym hasn’t recorded any membership or package payments yet."
      />
    );
  }
  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
        <p className="text-sm text-muted">{rows.length} payment{rows.length === 1 ? '' : 's'}</p>
        <p className="text-sm font-medium tabular text-ink">Collected {money(collected)}</p>
      </div>
      <div className="hidden grid-cols-[1.3fr_1fr_0.8fr_0.8fr_0.8fr] gap-4 border-b border-line px-5 py-2 text-eyebrow uppercase text-muted md:grid">
        <span>Member</span>
        <span>Plan</span>
        <span>Paid on</span>
        <span>Method</span>
        <span className="text-right">Amount</span>
      </div>
      <ul className="divide-y divide-line">
        {rows.map((p) => (
          <li key={p.id} className={cn('grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-3 md:grid-cols-[1.3fr_1fr_0.8fr_0.8fr_0.8fr]', p.voided && 'opacity-60')}>
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{p.memberName}</p>
              <p className="truncate text-sm text-muted md:hidden">
                {p.planName || (p.type === 'PERSONAL_TRAINING' ? 'Personal training' : 'Membership')} · {fmtDate(p.paidOn)}
              </p>
            </div>
            <span className="hidden truncate text-sm text-ink-soft md:block">
              {p.planName || (p.type === 'PERSONAL_TRAINING' ? 'Personal training' : 'Membership')}
              {p.isRenewal ? ' · renewal' : ''}
              {p.voided ? ' · voided' : ''}
            </span>
            <span className="hidden text-sm tabular text-ink-soft md:block">{fmtDate(p.paidOn)}</span>
            <span className="hidden text-sm text-ink-soft md:block">{payMethod(p.method)}</span>
            <span className={cn('justify-self-end text-sm font-medium tabular', p.voided ? 'text-muted line-through' : 'text-ink')}>
              {money(p.amount)}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
